// distanceClient.ts
// Thin client for the remote distance query API exposed by the
// CADViewer conversion server (routes/distance.js -> /api/distance).

import { DEMOS } from "../../../demos";
import { Config } from "../../../utils/config";

export interface DistancePlan {
  id: string;
  planDisplayName: string;
  propertyName?: string;
  planName?: string;
  unit?: string;
  metrics: string[]; // e.g. ["walking", "direct"]
}

export interface DistanceResult {
  plan: string;
  from: string;
  to: string;
  metric: string;
  distance: number;
  unit: string;
}

export interface DistanceApiError {
  error: string;
  missing?: "from" | "to";
  value?: string;
  details?: string;
}

export type DistanceQueryResponse =
  | { ok: true; data: DistanceResult }
  | { ok: false; status: number; error: DistanceApiError };

/**
 * Build the API base URL from the configured backend server URL and API path.
 * Tolerates trailing/leading slashes.
 */
export function buildDistanceApiBase(
  serverBackEndUrl: string,
  apiPath: string = "/api/distance"
): string {
  const server = (serverBackEndUrl || "").replace(/\/+$/, "");
  const path = apiPath.startsWith("/") ? apiPath : `/${apiPath}`;
  return `${server}${path}`;
}

const apiBaseFromConfig = (c?: Partial<Config>): string | null =>
  c && c.enableDistanceQuery && c.ServerBackEndUrl
    ? buildDistanceApiBase(c.ServerBackEndUrl, c.distanceQueryApiPath || "/api/distance")
    : null;

/**
 * Resolve the distance API base URL.
 *
 * The distance dataset/endpoint lives on the backend of whichever demo enables
 * the feature (currently /public_demo -> server.ai). The standalone full-page
 * route (/distance-query) resolves to the "default" demo config, which does NOT
 * host the endpoint - so we fall back to the first demo that enables the feature
 * instead of querying the wrong backend.
 */
export function resolveDistanceApiBase(currentConfig?: Partial<Config>): string {
  // 1) Current demo, if it owns the feature.
  const current = apiBaseFromConfig(currentConfig);
  if (current) return current;

  // 2) Any demo that enables the feature (e.g. public_demo -> server.ai).
  for (const demo of Object.values(DEMOS)) {
    const base = apiBaseFromConfig(demo.config as Partial<Config>);
    if (base) return base;
  }

  // 3) Last resort: current demo's backend.
  return buildDistanceApiBase(
    currentConfig?.ServerBackEndUrl || "",
    currentConfig?.distanceQueryApiPath || "/api/distance"
  );
}

async function getJson(url: string): Promise<{ status: number; body: any }> {
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    redirect: "follow",
  });
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  return { status: response.status, body };
}

/** Fetch the list of available distance plans (dropdown source). */
export async function fetchDistancePlans(
  apiBase: string
): Promise<DistancePlan[]> {
  const { status, body } = await getJson(`${apiBase}/plans`);
  if (status !== 200 || !body) {
    throw new Error(
      (body && body.error) || `Failed to load distance plans (HTTP ${status})`
    );
  }
  return Array.isArray(body) ? body : body.data || [];
}

/** Fetch the valid bay IDs (validation / autocomplete). Plan is fixed server-side. */
export async function fetchDistanceBays(
  apiBase: string,
  metric?: string
): Promise<string[]> {
  const params = new URLSearchParams();
  if (metric) params.set("metric", metric);
  const query = params.toString();
  const { status, body } = await getJson(`${apiBase}/bays${query ? `?${query}` : ""}`);
  if (status !== 200 || !body) {
    throw new Error(
      (body && body.error) || `Failed to load bays (HTTP ${status})`
    );
  }
  return body.bays || [];
}

/**
 * Run a distance query. Plan is fixed server-side, so only from/to/metric are sent.
 * Returns a discriminated result (never throws on 4xx).
 */
export async function queryDistance(
  apiBase: string,
  params: { from: string; to: string; metric?: string }
): Promise<DistanceQueryResponse> {
  const search = new URLSearchParams({
    from: params.from,
    to: params.to,
  });
  if (params.metric) search.set("metric", params.metric);

  const { status, body } = await getJson(`${apiBase}/query?${search.toString()}`);
  if (status === 200 && body && typeof body.distance === "number") {
    return { ok: true, data: body as DistanceResult };
  }
  return {
    ok: false,
    status,
    error: (body as DistanceApiError) || { error: `Request failed (HTTP ${status})` },
  };
}
